import React from 'react';
import I18N from '@leverege/i18n';
import { Layout } from '@leverege/plugin';
import { Button, Checkbox, IconUtil, Radio, ToggleButton } from '@leverege/ui-elements';
import { Toolbar, Util } from '@leverege/ui-plugin';
import { GlobalState } from '@leverege/ui-redux';
import { R } from '@leverege/value-resolver';
import { History } from '@leverege/molten-core';
import RoutesUtil from "../../../routes/Util.js";
import S from './ToolbarFactory.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  tIcon
} = I18N.ns('toolbar.factory.mobile');

/**
 * This factory will produce at the top level, toggle butons, buttons,
 * checkboxs, radiobuttons and popup components to support the various
 * types of actions. It will use a MenuFactory for all deep items
 */
export default class ToolbarFactory {
  constructor(options) {
    const opts = options || {};
    this.prefer = opts.prefer;
    this.preferIcon = this.prefer === 'icon';
    this.preferText = this.prefer === 'text';
    // this.blankIcon = opts.blankIcon === undefined ? 'fa fa-fw' : opts.blankIcon // used in vertical only
    this.vertical = opts.vertical === true;
    this.variant = opts.variant || 'toolbar';
    this.layout = opts.layout || {
      type: 'popup'
    };
    this.layoutVariant = opts.layoutVariant;
    this.toggleButtonVariant = opts.toggleButtonVariant || this.variant;
    this.buttonVariant = opts.buttonVariant || this.variant;
    this.checkboxVariant = opts.checkboxVariant || this.variant;
    this.radioButtonVariant = opts.radioButtonVariant || this.variant;
    this.popupVariant = opts.popupVariant || this.toggleButtonVariant;
    this.titleVariant = opts.titleVariant || this.variant;
    this.variantWith = opts.variantWith || Util.variantWith;
    if (opts.popupToggleVariant) {
      this.popupToggleVariant = opts.popupToggleVariant;
    } else {
      const tbv = this.toggleButtonVariant.split('|').map(v => `${v}Menu`).join('|');
      this.popupToggleVariant = `${tbv}|${this.toggleButtonVariant}`;
    }
    this.matchContext = opts.matchContext;
    this.stack = opts.stack;
  }
  onClick = evt => {
    const {
      action,
      context
    } = evt.data;
    if (typeof action?.perform === 'function') {
      action.perform({
        sourceEvent: evt.sourceEvent,
        ...evt.data
      });
      if (context?.onClose) context.onClose();
    }
  };
  createIcon(str) {
    if (str == null) {
      return null;
    }
    if (typeof str === 'string') {
      if (str.startsWith('data:') || str.startsWidth('http:') || str.startsWidth('https:') || str.endsWith('.png') || str.endsWith('.jpg') || str.endsWith('.gif') || str.endsWith('.svg')) {
        return /*#__PURE__*/_jsx("img", {
          src: str,
          width: this.iconSize
        }); // eslint-disable-line
      }
      return /*#__PURE__*/_jsx("i", {
        className: str
      });
    }
    if (typeof str === 'function') {
      return str();
    }
    return null;
  }
  createComponent(props) {
    const {
      action
    } = props;
    const {
      id,
      name,
      icon,
      iconOff,
      iconOn,
      disabled,
      visible,
      checkStyle,
      checked,
      accessory,
      variant,
      sublayout
    } = action.appearance(props);
    if (visible === false || icon == null && name == null) {
      return null;
    }
    const pIcon = this.preferIcon;
    const pText = this.preferText;
    const tip = pIcon && typeof name === 'string' ? name : null;
    const str = pIcon && icon ? null : name;
    const ioff = pText && name ? null : iconOff || icon;
    const ion = pText && name ? null : iconOn || icon;
    // Todo: If icons and vertical, leave space on ones without

    if (checkStyle === 'check') {
      const baseVariant = this.stack.isEmpty() ? this.checkboxVariant : this.checkboxVariant.split('|').map(v => `${v}Nested`).join('|');
      return /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Checkbox, {
          id: id,
          title: tip,
          variant: this.variantWith(baseVariant, variant),
          label: name,
          value: checked,
          disabled: disabled,
          onClick: this.onClick,
          eventData: props
        }, action.key)
      });
    }
    if (checkStyle === 'radio') {
      const baseVariant = this.stack.isEmpty() ? this.radioButtonVariant : this.radioButtonVariant.split('|').map(v => `${v}Nested`).join('|');
      return /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(Radio, {
          id: id,
          title: tip,
          variant: this.variantWith(baseVariant, variant),
          label: name,
          value: checked,
          disabled: disabled,
          onChange: this.onClick,
          eventData: props
        }, action.key)
      });
    }
    let more;
    if (sublayout) {
      const moreVariant = this.variant.split('|').map(v => `${v}More`).join('|');
      const moreIcon = tIcon('more', 'https://molten-ui-assets.storage.googleapis.com/more.svg');
      const onClick = () => this.stack.push({
        name,
        items: sublayout,
        matchContext: this.matchContext
      });
      more = /*#__PURE__*/_jsx(Button, {
        variant: moreVariant,
        icon: moreIcon,
        onClick: onClick
      });
    }
    const baseVariant = this.stack.isEmpty() ? this.toggleButtonVariant : this.toggleButtonVariant.split('|').map(v => `${v}Nested`).join('|');
    return /*#__PURE__*/_jsxs(ToggleButton, {
      id: id,
      variant: this.variantWith(baseVariant, variant),
      value: checked,
      disabled: disabled,
      onChange: this.onClick,
      iconOn: ion,
      iconOff: ioff,
      eventData: props,
      children: [/*#__PURE__*/_jsx("div", {
        className: S.title,
        children: str
      }), /*#__PURE__*/_jsxs("div", {
        className: S.accessories,
        children: [accessory, more]
      })]
    }, action.key);
  }
  createComponentProps(item, cxt) {
    const name = this.preferText && item.name ? null : R(item.name, cxt);
    const icon = this.preferIcon && item.icon ? null : R(item.icon, cxt);
    const iconR = tIcon('chevronRight', 'https://molten-ui-assets.storage.googleapis.com/chevron-right.svg');
    const accessory = IconUtil.create(iconR);
    const action = {
      handles: item.handles || (() => {
        return true;
      }),
      appearance: item.appearance || (() => {
        const match = cxt.clientProps.match;
        const checked = item.items.some(item => {
          const rPath = History.adjustPath(match ? RoutesUtil.resolve(item.path, match) : item.path);
          const cPath = GlobalState.get().router?.location.pathname;
          const checked = item.startsWith ? cPath.startsWith(`${rPath}/`) || cPath === rPath : cPath === rPath;
          return checked;
        });
        return {
          name,
          icon,
          accessory,
          checked
        };
      }),
      perform: item.perform || (() => {
        this.stack.push({
          ...item,
          matchContext: this.matchContext
        });
      })
    };
    const context = {
      ...cxt,
      onClose: undefined
    };
    return {
      action,
      context
    };
  }

  /**
   * Returns an array of items. Where groups occur, a Popup will be
   * created with submenus in it as necessary.
   * @param {PlacementTree} _layoutRoot the tree of actions
   */
  createComponents(_layoutRoot, cxt, level = 0) {
    const layoutRoot = level === 0 && !this.stack.isEmpty() ? this.stack.top() : _layoutRoot; // Ugly.

    if (layoutRoot == null) {
      return null;
    }
    let separator = null;
    const r = [];
    const items = Array.isArray(layoutRoot) ? layoutRoot : layoutRoot.items;
    items.forEach((item, index) => {
      let cmp = null;
      let sort = null;
      let sectionName = null;
      if (item.type === Layout.NODE_TYPE) {
        // We need to know if the menu should show up. Call handles
        // TODO: We can do the handles above this call to remove item
        // from this location
        if (!Util.handles(item.items, cxt)) {
          return;
        }
        cmp = this.createComponent(this.createComponentProps(item, cxt));
      } else {
        const {
          customComponent: Component
        } = item;
        if (item.handles == null || !item.handles(cxt)) {
          return;
        }
        sort = R(item?.layout?.sort, cxt);
        sectionName = R(item?.layout?.sectionName, cxt);
        if (typeof item.getActions === 'function') {
          cmp = this.createComponents(item.getActions(cxt), cxt, level + 1);
        } else if (Component == null) {
          cmp = this.createComponent({
            action: item,
            context: cxt
          });
        } else {
          cmp = /*#__PURE__*/_jsx(Component, {
            context: cxt,
            action: item,
            factory: this
          }, item.key);
        }
      }
      const isArray = Array.isArray(cmp);
      if (cmp == null || isArray && cmp.length === 0) {
        return;
      }

      // Do we have a section?
      const idx = sort ? sort.lastIndexOf('.') : -1;
      const key = idx < 0 ? null : sort.substring(0, idx);
      const vtl = this.vertical;
      const vrt = this.variant;
      const showHdr = !this.preferIcon || !this.hideHeaderWhenIconOnly;
      if (cmp != null && r.length > 0) {
        // do we need to add a separator?
        if (idx >= 0) {
          // If the key is not the same as separator, we are in a new section. Add separator.
          if (key !== separator) {
            separator = key;
            r.push(/*#__PURE__*/_jsx(Toolbar.Divider, {
              variant: vrt,
              vertical: vtl
            }, `sep-${key}`));
            if (showHdr && sectionName) {
              r.push(/*#__PURE__*/_jsx(Toolbar.Header, {
                variant: vrt,
                vertical: vtl,
                children: sectionName
              }, `hdr-${key}`));
            } else if (sectionName) {
              // r.push( <Toolbar.Divider variant={vrt} vertical={vtl} key={`hdr-${key}`} /> )
            }
            if (isArray) {
              r.push(...cmp);
            } else {
              r.push(cmp);
            }
            return;
          }
          // We are in the same separator. Just add component
          if (isArray) {
            r.push(...cmp);
          } else {
            r.push(cmp);
          }
          return;
        }
        if (separator) {
          // We are not in a section, but the section is active. Add closing separator
          separator = '__not_grouped__';
          // eslint-disable-next-line react/no-array-index-key
          // r.push( <Toolbar.Divider variant={this.variant} vertical={this.vertical} key={`sep-${index}`}/> )
          if (isArray) {
            r.push(...cmp);
          } else {
            r.push(cmp);
          }
          return;
        }
      }
      if (cmp) {
        separator = key;
        if (r.length === 0 && key && showHdr && sectionName) {
          r.push(/*#__PURE__*/_jsx(Toolbar.Header, {
            variant: vrt,
            vertical: vtl,
            children: sectionName
          }, `hdr-${key}`));
        }
        if (isArray) {
          r.push(...cmp);
        } else {
          r.push(cmp);
        }
      }
    });
    return r;
  }
}